/**
 * SVGRefContext - Provides access to the SVG element ref
 *
 * This context eliminates the need to manually thread the SVG ref through
 * component props. Components can access the ref using the useSVGRef hook.
 *
 * Phase 5 of arrow drawing refactor.
 */

import React, { createContext, useContext } from 'react';

// ============================================================================
// Context Type
// ============================================================================

export interface SVGRefContextValue {
  /** Reference to the SVG element (React 19: RefObject<T | null>) */
  svgRef: React.RefObject<SVGSVGElement | null>;
}

const SVGRefContext = createContext<SVGRefContextValue | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

export interface SVGRefProviderProps {
  /** Child components */
  children: React.ReactNode;
  /** Reference to the SVG element (React 19: RefObject<T | null>) */
  svgRef: React.RefObject<SVGSVGElement | null>;
}

// ============================================================================
// Provider Component
// ============================================================================

/**
 * SVGRefProvider - Provides SVG element ref via context
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const svgRef = useRef<SVGSVGElement>(null);
 *
 *   return (
 *     <SVGRefProvider svgRef={svgRef}>
 *       <svg ref={svgRef}>
 *         <ComponentThatNeedsSVGRef />
 *       </svg>
 *     </SVGRefProvider>
 *   );
 * }
 * ```
 */
export function SVGRefProvider({ children, svgRef }: SVGRefProviderProps) {
  const contextValue: SVGRefContextValue = {
    svgRef,
  };

  return (
    <SVGRefContext.Provider value={contextValue}>
      {children}
    </SVGRefContext.Provider>
  );
}

// ============================================================================
// Context Hook
// ============================================================================

/**
 * Hook to access SVG element ref from context
 *
 * @param options - Configuration options
 * @param options.optional - If true, returns null instead of throwing when context is missing
 * @throws Error if used outside SVGRefProvider (unless optional=true)
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { svgRef } = useSVGRef();
 *
 *   const handleClick = (event: React.MouseEvent) => {
 *     if (!svgRef.current) return;
 *     const svgPos = screenToSVG(svgRef.current, event.clientX, event.clientY);
 *     console.log('SVG coordinates:', svgPos);
 *   };
 *
 *   return <circle onClick={handleClick} />;
 * }
 * ```
 */
export function useSVGRef(options?: { optional?: boolean }): SVGRefContextValue | null {
  const context = useContext(SVGRefContext);

  if (!context && !options?.optional) {
    throw new Error('useSVGRef must be used within SVGRefProvider');
  }

  return context;
}
